import React, { useRef, useState, useEffect, useCallback } from "react";
import {
  FiUser,
  FiActivity,
  FiClipboard,
  FiBarChart2,
  FiCoffee,
  FiTrendingUp,
  FiCamera,
  FiChevronLeft,
  FiChevronRight,
} from "react-icons/fi";
import { TabButton } from "./StudentManagementModalComponents";
import { vibrateTactile } from "../../../helpers/vibrate";

export type StudentManagementTab =
  | "avaliation"
  | "workout"
  | "profile"
  | "tracking"
  | "food"
  | "comparison"
  | "photos";

interface StudentManagementModalTabsProps {
  activeTab: StudentManagementTab;
  onTabChange: (tab: StudentManagementTab) => void;
  avaliationCount: number;
  workoutCount: number;
  isVisible?: boolean;
}

export default function StudentManagementModalTabs({
  activeTab,
  onTabChange,
  avaliationCount,
  workoutCount,
  isVisible = true,
}: StudentManagementModalTabsProps) {
  const tabsScrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateTabsScrollState = useCallback(() => {
    const el = tabsScrollRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setCanScrollLeft(scrollLeft > 2);
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 2);
  }, []);

  useEffect(() => {
    if (!isVisible) return;
    const el = tabsScrollRef.current;
    const run = () => updateTabsScrollState();
    run();
    const t = setTimeout(run, 100);
    if (!el) return () => clearTimeout(t);
    const ro = new ResizeObserver(run);
    ro.observe(el);
    el.addEventListener("scroll", run, { passive: true });
    return () => {
      clearTimeout(t);
      ro.disconnect();
      el.removeEventListener("scroll", run);
    };
  }, [isVisible, updateTabsScrollState]);

  const scrollTabs = useCallback((direction: "left" | "right") => {
    vibrateTactile();
    const el = tabsScrollRef.current;
    if (!el) return;
    const step = el.clientWidth * 0.6;
    el.scrollBy({
      left: direction === "left" ? -step : step,
      behavior: "smooth",
    });
  }, []);

  const handleTabChange = useCallback(
    (tab: StudentManagementTab) => {
      onTabChange(tab);
    },
    [onTabChange],
  );

  return (
    <div className="relative border-b border-stroke bg-gray-50/50 dark:bg-gray-900/30 dark:border-strokedark">
      {canScrollLeft && (
        <button
          type="button"
          onClick={() => scrollTabs("left")}
          className="absolute left-1 top-1/2 -translate-y-1/2 z-20 flex items-center justify-center w-9 h-9 min-w-[44px] min-h-[44px] rounded-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 shadow-md text-gray-600 dark:text-gray-300 hover:text-purple-600 dark:hover:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/30 hover:border-purple-200 dark:hover:border-purple-700/50 hover:shadow-lg active:scale-95 transition-all duration-200 sm:hidden touch-manipulation"
          aria-label="Ver mais abas à esquerda"
        >
          <FiChevronLeft
            className="w-5 h-5 flex-shrink-0"
            strokeWidth={2.5}
          />
        </button>
      )}
      {canScrollRight && (
        <button
          type="button"
          onClick={() => scrollTabs("right")}
          className="absolute right-1 top-1/2 -translate-y-1/2 z-20 flex items-center justify-center w-9 h-9 min-w-[44px] min-h-[44px] rounded-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 shadow-md text-gray-600 dark:text-gray-300 hover:text-purple-600 dark:hover:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/30 hover:border-purple-200 dark:hover:border-purple-700/50 hover:shadow-lg active:scale-95 transition-all duration-200 sm:hidden touch-manipulation"
          aria-label="Ver mais abas à direita"
        >
          <FiChevronRight
            className="w-5 h-5 flex-shrink-0"
            strokeWidth={2.5}
          />
        </button>
      )}
      <div
        className="absolute left-0 top-0 bottom-0 w-6 sm:w-8 bg-gradient-to-r from-gray-50 to-transparent dark:from-gray-900/30 pointer-events-none z-10 sm:hidden"
        aria-hidden
      />
      <div
        className="absolute right-0 top-0 bottom-0 w-6 sm:w-8 bg-gradient-to-l from-gray-50 to-transparent dark:from-gray-900/30 pointer-events-none z-10 sm:hidden"
        aria-hidden
      />

      <div
        ref={tabsScrollRef}
        className="overflow-x-auto scrollbar-hide overflow-y-hidden -webkit-overflow-scrolling-touch snap-x snap-mandatory scroll-smooth px-3 py-3 sm:px-4 sm:py-3 md:px-6"
      >
        <div className="flex gap-2 sm:gap-2.5 md:gap-3 min-w-max sm:min-w-0">
          <TabButton
            active={activeTab === "profile"}
            onClick={() => handleTabChange("profile")}
            icon={FiUser}
            label="Perfil"
          />
          <TabButton
            active={activeTab === "avaliation"}
            onClick={() => handleTabChange("avaliation")}
            icon={FiActivity}
            label="Avaliações"
            count={avaliationCount}
          />
          <TabButton
            active={activeTab === "workout"}
            onClick={() => handleTabChange("workout")}
            icon={FiClipboard}
            label="Treinos"
            count={workoutCount}
          />
          <TabButton
            active={activeTab === "tracking"}
            onClick={() => handleTabChange("tracking")}
            icon={FiBarChart2}
            label="Atividades"
          />
          <TabButton
            active={activeTab === "food"}
            onClick={() => handleTabChange("food")}
            icon={FiCoffee}
            label="Alimentação"
          />
          <TabButton
            active={activeTab === "comparison"}
            onClick={() => handleTabChange("comparison")}
            icon={FiTrendingUp}
            label="Comparativo"
          />
          <TabButton
            active={activeTab === "photos"}
            onClick={() => handleTabChange("photos")}
            icon={FiCamera}
            label="Fotos mensais"
          />
        </div>
      </div>
    </div>
  );
}
